import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Globe, Server, Radio, Wifi, Phone,
  ArrowRight, CheckCircle, Building2, Zap,
  BarChart3, Lock, Users, Activity
} from 'lucide-react';
import SEO from '../components/SEO';

const carriers = [
  { name: "Tier-1 Global Carrier", region: "EMEA", subscribers: "120M+", modules: ["SS7 Protection", "GSM Fraud", "SIM Swap Prevention", "IoT Security"] },
  { name: "National Mobile Operator", region: "APAC", subscribers: "85M+", modules: ["Mobile Protection", "Financial Fraud", "Network Analytics", "5G Security"] },
  { name: "Regional Telco", region: "Americas", subscribers: "30M+", modules: ["CSP Operations", "Roaming Fraud", "AI Security Gateway", "Compliance"] }
];

const CarriersPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <SEO title="Carrier Solutions | SecureSphere by Infuse.AI" description="Carrier-grade security solutions for telecom operators. SS7, Diameter, GTP protection, GSM fraud detection, and AI-powered network security at scale." />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition">
            <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
          </button>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/about')} className="text-sm text-neutral-300 hover:text-white transition hidden md:block">About</button>
            <button onClick={() => navigate('/login')} className="px-5 py-2.5 bg-[#F26419] text-white font-semibold rounded-lg text-sm hover:bg-[#F7B731] hover:text-black transition-all">Sign In</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2a1f1a] to-[#1a1a1a]" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-sm font-medium mb-6">Carrier Solutions</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Carrier-Grade<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F7B731] to-[#F26419]">Security at Scale</span></h1>
            <p className="text-lg text-neutral-400 max-w-3xl mx-auto">Purpose-built for telecom operators managing millions of subscribers. Protect your network, subscribers, and revenue with SecureSphere.</p>
          </motion.div>
        </div>
      </section>

      {/* Key Capabilities */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Telecom Security Suite</h2>
          <p className="text-neutral-400 text-center max-w-2xl mx-auto mb-16">End-to-end protection across every protocol, network layer, and subscriber touchpoint.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Radio, title: "SS7 & Diameter Protection", desc: "Real-time monitoring and blocking of signaling attacks including location tracking, call interception, and SMS interception.", stat: "99.9% block rate" },
              { icon: Phone, title: "GSM Fraud Prevention", desc: "SIM swap detection, IMSI catcher alerts, OTP fraud blocking, and roaming fraud prevention with AI-driven behavioral analysis.", stat: "$4.2M avg. savings/quarter" },
              { icon: Shield, title: "Network Security", desc: "GTP tunnel security, IPX protection, 5G core security, and cross-protocol threat correlation across your entire network.", stat: "10B+ events/day" },
              { icon: Wifi, title: "IoT Device Security", desc: "Carrier-managed IoT security for connected devices including firmware scanning, anomaly detection, and network segmentation.", stat: "500K+ devices protected" },
              { icon: BarChart3, title: "CSP Operations Center", desc: "Real-time SOC integration, automated incident response, eSIM lifecycle management, and network slice security.", stat: "8ms avg. response time" },
              { icon: Lock, title: "AI Workload Security", desc: "Protect AI/ML workloads running on your network. Prompt injection detection, model security, and AI governance for enterprise customers.", stat: "200K+ API calls/day" },
            ].map((cap) => (
              <motion.div key={cap.title || c.name || Math.random()} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#242424] border border-white/10 rounded-2xl p-6 hover:border-orange-500/30 transition-all">
                <cap.icon className="w-8 h-8 text-orange-400 mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">{cap.title}</h3>
                <p className="text-neutral-400 text-sm mb-4 leading-relaxed">{cap.desc}</p>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#F7B731]" />
                  <span className="text-sm text-[#F7B731] font-semibold">{cap.stat}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Carrier Case Studies */}
      <section className="py-20 bg-[#242424]/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Trusted by Carriers Worldwide</h2>
          <p className="text-neutral-400 text-center max-w-2xl mx-auto mb-12">Our platform protects over 235 million subscribers across 3 continents.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {carriers.map((c, i) => (
              <motion.div key={cap.title || c.name || Math.random()} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold">{c.name}</h3>
                  <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full">{c.region}</span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-neutral-500" />
                  <span className="text-neutral-400 text-sm">{c.subscribers} subscribers</span>
                </div>
                <div className="space-y-2">
                  {c.modules.map((mod, j) => (
                    <div key={`mod-${j}`} className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-sm text-neutral-300">{mod}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#F26419]/10 to-[#F7B731]/10 border border-orange-500/20 rounded-3xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Flexible Deployment Options</h2>
            <p className="text-neutral-400 max-w-2xl mx-auto mb-8">Deploy SecureSphere as a cloud SaaS, on-premises appliance, or hybrid model tailored to your carrier infrastructure and compliance requirements.</p>
            <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto mb-8">
              {[
                { label: "Cloud SaaS", icon: Globe },
                { label: "On-Premises", icon: Server },
                { label: "Hybrid", icon: Activity },
              ].map((opt) => (
                <div key={opt.label} className="text-center">
                  <opt.icon className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                  <p className="text-white font-semibold">{opt.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:info@infuse.net.in" className="px-8 py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full text-lg hover:shadow-[0_0_40px_rgba(242,100,25,0.5)] transition-all flex items-center gap-2">
                Talk to Carrier Sales <ArrowRight className="w-5 h-5" />
              </a>
              <button onClick={() => navigate('/demo')} className="px-8 py-4 bg-white/5 border border-white/20 text-white font-medium rounded-full text-lg hover:bg-white/10 transition-all">Watch Platform Demo</button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#242424] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-neutral-500">&copy; 2026 Infuse Technologies. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-sm text-neutral-500 hover:text-white transition">Home</button>
            <button onClick={() => navigate('/about')} className="text-sm text-neutral-500 hover:text-white transition">About</button>
            <a href="mailto:info@infuse.net.in" className="text-sm text-neutral-500 hover:text-white transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CarriersPage;
